/**
 * useCategoryCRUD Hook
 *
 * 카테고리 CRUD 작업을 위한 React Hook
 *
 * 책임:
 * - 카테고리 데이터 로드
 * - 카테고리 생성/수정/삭제
 * - 로딩/에러 상태 관리
 * - 2단계 계층 구조 (대분류/소분류) 지원
 */

import { useState } from 'react'
import type { Category } from '../types/Category'
import { categoryRepository } from '../repositories'

/**
 * useCategoryCRUD Return Type
 */
export interface UseCategoryCRUD {
  /** 카테고리 목록 (대분류, children에 소분류 포함) */
  data: Category[]
  /** 로딩 상태 */
  loading: boolean
  /** 에러 상태 */
  error: Error | null
  /** 전체 카테고리 로드 */
  loadAll: () => Promise<void>
  /** 타입별 카테고리 로드 (income/expense) */
  loadByType: (type: 'income' | 'expense') => Promise<void>
  /** 새 카테고리 생성 */
  create: (input: Omit<Category, 'id'>) => Promise<Category>
  /** 카테고리 수정 */
  update: (id: string, input: Partial<Category>) => Promise<Category>
  /** 카테고리 삭제 */
  remove: (id: string) => Promise<void>
}

/**
 * 카테고리 CRUD Hook
 *
 * @returns CRUD 작업 메서드 및 상태
 *
 * @example
 * ```tsx
 * function CategoriesPage() {
 *   const { data, loading, error, loadByType, create, update, remove } = useCategoryCRUD()
 *
 *   useEffect(() => {
 *     loadByType('expense')
 *   }, [])
 *
 *   const handleCreateMajor = async () => {
 *     await create({
 *       name: '교통',
 *       icon: '🚗',
 *       type: 'expense',
 *       level: 'major',
 *       sortOrder: 10
 *     })
 *     await loadByType('expense')
 *   }
 *
 *   const handleCreateMinor = async (parentId: string) => {
 *     await create({
 *       name: '택시',
 *       icon: '🚕',
 *       type: 'expense',
 *       level: 'minor',
 *       parentId,
 *       sortOrder: 1
 *     })
 *     await loadByType('expense')
 *   }
 * }
 * ```
 */
export function useCategoryCRUD(): UseCategoryCRUD {
  const [data, setData] = useState<Category[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * 전체 카테고리 로드
   */
  const loadAll = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await categoryRepository.findAll()
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
      throw err
    } finally {
      setLoading(false)
    }
  }

  /**
   * 타입별 카테고리 로드
   */
  const loadByType = async (type: 'income' | 'expense') => {
    try {
      setLoading(true)
      setError(null)
      const result = await categoryRepository.findByType(type)
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
      throw err
    } finally {
      setLoading(false)
    }
  }

  /**
   * 새 카테고리 생성
   */
  const create = async (input: Omit<Category, 'id'>): Promise<Category> => {
    try {
      setLoading(true)
      setError(null)
      const created = await categoryRepository.create(input)
      return created
    } catch (err) {
      setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
      throw err
    } finally {
      setLoading(false)
    }
  }

  /**
   * 카테고리 수정
   */
  const update = async (
    id: string,
    input: Partial<Category>
  ): Promise<Category> => {
    try {
      setLoading(true)
      setError(null)
      const updated = await categoryRepository.update(id, input)
      return updated
    } catch (err) {
      setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
      throw err
    } finally {
      setLoading(false)
    }
  }

  /**
   * 카테고리 삭제
   */
  const remove = async (id: string): Promise<void> => {
    try {
      setLoading(true)
      setError(null)
      await categoryRepository.delete(id)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
      throw err
    } finally {
      setLoading(false)
    }
  }

  return {
    data,
    loading,
    error,
    loadAll,
    loadByType,
    create,
    update,
    remove,
  }
}
